/* import __COLOCATED_TEMPLATE__ from './checklist-step.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import STEPS from 'embercom/lib/home/steps';
import { type Step } from 'embercom/objects/onboarding/checklist/step';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';

export interface Args {
  step: Step;
  isFirstStep: boolean;
  stepAccordion: any;
  collapseChecklist: () => void;
  shouldAnimate: boolean;
  currentlyOpenStepId: string | undefined;
  onCTASuccess?: (stepIdentifier: string) => void;
}

export default class ChecklistStep extends Component<Args> {
  @service declare router: RouterService;
  @service declare appService: $TSFixMe;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare intercomEventService: any;
  @service declare intl: IntlService;

  get isChecked() {
    return this.args.step.state === 'completed';
  }

  get app() {
    return this.appService.app;
  }

  get isStepDisabled() {
    return this.args.step.identifier === this.args.currentlyOpenStepId;
  }

  get descriptionClass() {
    return this.isChecked ? 'onboarding__floating-widget__completed-step-desc' : 'text-base';
  }

  @action
  primaryClickAction() {
    let stepIdentifier = this.args.step.identifier;
    let stepData = STEPS[stepIdentifier as keyof typeof STEPS] as any;
    if (!(stepIdentifier === 'great_guidance_invite_teammates' && this.appService.app.onPricing5)) {
      stepData.primaryAction(this.router, this.args.step, this.appService.app);
    }
    this.greatGuidanceService.nextStepId = this.args.step.identifier;
    this.args.collapseChecklist();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'getting_started_homepage',
      object: `${this.args.step.identifier}-cta`,
      section: 'onboarding.getting-started.widget',
      place: 'onboarding.getting-started',
      step_state: this.args.step?.state,
    });
    if (this.args.onCTASuccess) {
      this.args.onCTASuccess(this.args.step.identifier);
    }
  }

  get isZendeskPlatform() {
    return this.appService.app.hasStandalonePlatform('zendesk');
  }

  get isSalesforcePlatform() {
    return this.appService.app.hasStandalonePlatform('salesforce');
  }

  get platform() {
    if (this.isZendeskPlatform) {
      return this.intl.t('onboarding.getting-started.platforms.zendesk.name');
    } else if (this.isSalesforcePlatform) {
      return this.intl.t('onboarding.getting-started.platforms.salesforce.name');
    }
    return '';
  }

  get translationsPrefix(): string {
    if (this.args.step.identifier === 'fin_sa_onboarding_connect_channels_and_turn_fin_live') {
      if (this.isZendeskPlatform) {
        return `onboarding.getting-started.steps.${this.args.step.identifier}.zendesk`;
      } else if (this.isSalesforcePlatform) {
        return `onboarding.getting-started.steps.${this.args.step.identifier}.salesforce`;
      } else {
        return `onboarding.getting-started.steps.${this.args.step.identifier}.generic`;
      }
    }
    return `onboarding.getting-started.steps.${this.args.step.identifier}`;
  }

  get descriptionTranslationKey(): string {
    if (
      this.args.step.identifier === 'fin_sa_onboarding_add_support_content' &&
      !this.isZendeskPlatform &&
      !this.isSalesforcePlatform
    ) {
      return `${this.translationsPrefix}.generic-description-widget`;
    }
    return `${this.translationsPrefix}.description-widget`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Onboarding::FloatingWidget::ChecklistStep': typeof ChecklistStep;
    'onboarding/floating-widget/checklist-step': typeof ChecklistStep;
  }
}
